import React, { useState, useEffect } from "react";
import { Outlet, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
    ChartBar, UserList, Envelope, Briefcase, SignOut, Crown, Calendar, Users,
    Stethoscope, Headset, ClockClockwise, Factory,
    CaretDown, Tag, Receipt, Handshake, Bank, Package,
    UserPlus, ClipboardText, Scan, Warning, Gift,
    ChartLineUp, Broadcast, Newspaper, Storefront,
    SidebarSimple, CheckSquareOffset, WhatsappLogo, SignIn,
    Pulse, Faders,
} from "@phosphor-icons/react";
import { InviteUserDialog } from "@/components/admin/InviteUserDialog";
import EscalationsBell from "@/components/admin/EscalationsBell";
import ReviewQueueBell from "@/components/admin/ReviewQueueBell";
import InvisiqueLogo from "@/components/site/Logo";
import ErrorBoundary from "@/components/site/ErrorBoundary";

// iter100.156d — Sidebar collapse state.
// iter100.198a — Persistence switched from `localStorage` →
// `sessionStorage` per user direction: every fresh login lands the
// admin on an EXPANDED sidebar (labels = mental map for the portal)
// with the automatic "collapsed on ≤1440 px laptop" default still
// applied. If they collapse it that preference stays only for this
// browser session. Next login (in a new tab, after logout, or after
// tab close) resets to the viewport default. AuthContext's `login()`
// + `logout()` also clear the key explicitly so a logout-then-relogin
// in the same tab still resets. Mirrors PortalLayout.jsx.
const ADMIN_COLLAPSED_KEY = "invq_admin_sidebar_collapsed";
/* iter100.78 — Collapsible sidebar groups. Each admin sidebar group
   (Work, Content, Operations, Admin, Insights, Danger zone) can be
   folded to just its header title so a super-admin can focus on the
   section they're actively working in. Persisted in localStorage. */
const ADMIN_GROUPS_COLLAPSED_KEY = "invq_admin_sidebar_groups_collapsed";

/* Group NAV items into buckets keyed by group label. The first item
   in each bucket carries the `groupLabel`; every subsequent item until
   the next `groupLabel` belongs to the same group. Returns the ordered
   list `[{ label, items }]` so the render loop can iterate cleanly. */
function _groupNav(nav) {
    const out = [];
    let current = null;
    for (const item of nav) {
        if (item.groupLabel) {
            current = { label: item.groupLabel, items: [item] };
            out.push(current);
        } else if (current) {
            current.items.push(item);
        } else {
            // Ungrouped prefix (shouldn't happen with our NAV, but be defensive)
            current = { label: null, items: [item] };
            out.push(current);
        }
    }
    return out;
}

/* Admin sidebar IA. Top-level items have either `to` (route) or `children`
   (a submenu). Submenus expand when one of their child routes is active. */
const NAV = [
    // WORK — daily ops
    { to: "/admin", icon: ChartBar, label: "Overview", end: true, groupStart: true, groupLabel: "Work" },
    { to: "/admin/leads", icon: UserList, label: "Leads" },
    // iter100.250 — Referrals restored as its own sidebar item after the
    // "embedded as a tab in Leads" experiment (iter100.228) produced a
    // jarring "two pages in one" mismatch. AdminReferrals is a rich
    // sub-app (doctor demo-links, reward approvals, invitations) and
    // deserves its own home right next to Leads.
    { to: "/admin/referrals", icon: Gift, label: "Referrals" },
    { to: "/admin/appointments", icon: Calendar, label: "Appointments" },
    // iter97.88 — IO Scanning Service ops.
    //   /admin/scan-visits → triage + assign-to-technician queue.
    // iter100.227 — Standalone /admin/technicians tab removed. Scan
    // technicians are managed alongside every other CoE role inside
    // Team & Access (/admin/coe?tab=people&filter=technician). Old
    // bookmarks redirect (see App.js).
    { to: "/admin/scan-visits", icon: Scan, label: "Scan Visits" },
    { to: "/admin/patients", icon: Users, label: "Patients" },
    { to: "/admin/providers", icon: Stethoscope, label: "Providers" },
    // iter100.262 — Verify Doctors sidebar item removed. The verification
    // queue was merged into /admin/providers as a 5th "Pending
    // verification" tile so all provider ops live in one command center.
    // CONTENT — editorial + microsite
    // iter100.228 — Consolidated 3 review queues (Content Review + Course
    // Reviews + Case Validations) into one Approvals command center at
    // /admin/approvals. Legacy routes still work (redirect).
    { to: "/admin/approvals", icon: CheckSquareOffset, label: "Approvals", groupStart: true, groupLabel: "Content" },
    // iter100.121 — Landing page CMS (counsellor spotlight + copy + SLA).
    // One place for everything the homepage lead-capture card renders.
    // iter100.206 — Renamed sidebar label "Landing Page" → "Marketing"
    // since this hub now covers Testimonials, Media Library, and
    // Why Invisique CMS (Built by Leaders + Certifications) too.
    // iter101.40 — Renamed back to "Landing CMS" per user request.
    { to: "/admin/landing-page", icon: Storefront, label: "Landing CMS" },
    // iter100.153 — Blog manager (list + editor with live Markdown preview).
    { to: "/admin/blog", icon: Newspaper, label: "Blog" },
    // iter100.156f — Live Sessions / webinar scheduling (external URL).
    { to: "/admin/webinars", icon: Broadcast, label: "Live Sessions" },
    // OPS — production, money, stock
    // iter97.21 — "Cases" + the placeholder "Production Dashboard" merged
    // into a single Production Control Center at /admin/cases. The legacy
    // /admin/production route now redirects to /admin/cases (see App.js).
    // iter97.31 — Renamed to "Aligner Cases" so the label matches what the
    // page actually shows (all cases across the lifecycle, not just
    // production).
    { to: "/admin/cases", icon: Factory, label: "Aligner Cases", groupStart: true, groupLabel: "Operations" },
    // iter101.32 — Service Setup owns everything that powers the case-creation
    // wizard (Pricing tiers/promos/priority tiers/wizard cards/wizard copy AND
    // plan-template sections/fields/clinical thresholds). Moved out of Finance
    // (Pricing tab) + Admin (Plan Templates) to give first-time admins ONE
    // place to configure the service before running day-to-day ops.
    { to: "/admin/service-setup", icon: Faders, label: "Service Setup" },
    { to: "/admin/finance", icon: Bank, label: "Finance" },
    { to: "/admin/inventory", icon: Package, label: "Inventory" },
    // ADMIN — team, tooling, messaging, audit
    // iter97.31 — Removed the "Tracking" submenu from the nav per user
    // request. The two pages (/admin/tracking/aligner and
    // /admin/tracking/patient-analytics) remain reachable by URL/links
    // from elsewhere; they're just no longer surfaced in the sidebar.
    { to: "/admin/coe", icon: Headset, label: "Team & Access", groupStart: true, groupLabel: "Admin" },
    { to: "/admin/messages", icon: Envelope, label: "Messages" },
    { to: "/admin/whatsapp", icon: WhatsappLogo, label: "WhatsApp" },
    { to: "/admin/audit", icon: ClockClockwise, label: "Audit Log" },
    // iter101.4 — Impersonation audit — WHO signed in as WHOM, WHEN, from WHERE.
    { to: "/admin/impersonation-log", icon: SignIn, label: "Impersonation Log" },
    // INSIGHTS — analytics + live presence
    // iter98.99du — Product analytics dashboard. Home for conversion funnels
    // (currently: Book Scan Visit; future: case-submit, plan-approval, etc.)
    { to: "/admin/analytics", icon: ChartLineUp, label: "Analytics", groupStart: true, groupLabel: "Insights" },
    // iter101.25 — Recent Activity moved out of Overview into its own
    // sidebar tab so admins can jump straight to a live feed of every
    // signal on the platform without scrolling past 5 sections.
    { to: "/admin/activity-history", icon: Pulse, label: "Activity Feed" },
    // iter100 — real-time "who's online" heartbeat roster (all roles).
    { to: "/admin/presence", icon: Broadcast, label: "Who's online" },
    // 2026-07-07 — Removed standalone "Roles & Permissions" sidebar entry.
    // The static permissions reference now lives as the "Permissions matrix"
    // tab inside Team & Access (/admin/coe?tab=permissions-matrix). The
    // /admin/access-control and /admin/permissions routes still exist as
    // redirects for legacy bookmarks (see App.js).
    // iter98.65 — Destructive ops. Lives at the bottom so it's visually
    // de-emphasized vs. day-to-day work; the page itself has its own
    // type-RESET confirmation gate.
    { to: "/admin/danger-zone", icon: Warning, label: "Danger zone", danger: true, groupStart: true, groupLabel: "Danger zone" },
];

function SubmenuItem({ item, collapsed }) {
    const { pathname } = useLocation();
    const isInside = pathname.startsWith(item.prefix);
    const [open, setOpen] = useState(isInside);

    // Keep open synced when navigating from a different section into this one
    useEffect(() => {
        if (isInside) setOpen(true);
    }, [isInside]);

    // Collapsed rail: parent shows as icon-only + first child link
    // (submenu can't expand in an icon rail). Clicking navigates to the
    // first child directly, which is the most common expectation.
    if (collapsed) {
        const first = item.children?.[0];
        if (!first) return null;
        return (
            <NavLink
                to={first.to}
                title={`${item.label} — ${first.label}`}
                data-testid={`admin-nav-group-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                    `w-full flex items-center justify-center h-10 rounded-xl transition-colors ${
                        isActive || isInside ? "bg-primary text-white" : "text-foreground hover:bg-secondary"
                    }`
                }
            >
                <item.icon size={18} weight="duotone" />
            </NavLink>
        );
    }

    return (
        <div data-testid={`admin-nav-group-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
            <button
                onClick={() => setOpen((o) => !o)}
                data-testid={`admin-nav-toggle-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                    isInside ? "bg-primary/10 text-primary font-semibold" : "text-foreground hover:bg-secondary"
                }`}
            >
                <item.icon size={18} weight="duotone" />
                <span className="flex-1 text-left">{item.label}</span>
                <CaretDown
                    size={14}
                    className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <div className="ml-7 mt-1 mb-1 space-y-0.5 border-l border-border pl-3">
                    {item.children.map((c) => (
                        <NavLink
                            key={c.to}
                            to={c.to}
                            data-testid={`admin-nav-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) =>
                                `block px-2.5 py-1.5 rounded-md text-[13px] transition-colors ${
                                    isActive ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                                }`
                            }
                        >
                            {c.label}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminLayout() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [inviteOpen, setInviteOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
        // iter100.267 — Default to expanded universally across all
        // portals per user direction ("by default keep the sidebar
        // expanded for all"). Drop any legacy localStorage value so
        // returning users get the fresh behaviour immediately.
        try { localStorage.removeItem(ADMIN_COLLAPSED_KEY); } catch { /* noop */ }
        const stored = sessionStorage.getItem(ADMIN_COLLAPSED_KEY);
        if (stored === "1") return true;
        if (stored === "0") return false;
        return false;
    });
    useEffect(() => {
        sessionStorage.setItem(ADMIN_COLLAPSED_KEY, collapsed ? "1" : "0");
    }, [collapsed]);

    /* iter100.78 — Which sidebar groups the admin has folded to their
       header title. Stored as an array of labels in localStorage. */
    const [groupsCollapsed, setGroupsCollapsed] = useState(() => {
        try {
            const raw = localStorage.getItem(ADMIN_GROUPS_COLLAPSED_KEY);
            if (!raw) return new Set();
            const parsed = JSON.parse(raw);
            return new Set(Array.isArray(parsed) ? parsed : []);
        } catch {
            return new Set();
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(ADMIN_GROUPS_COLLAPSED_KEY, JSON.stringify(Array.from(groupsCollapsed)));
        } catch { /* silent */ }
    }, [groupsCollapsed]);
    const toggleGroup = (label) => {
        setGroupsCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(label)) next.delete(label);
            else next.add(label);
            return next;
        });
    };

    /* Precompute the grouped NAV so the render loop can iterate cleanly. */
    const grouped = React.useMemo(() => _groupNav(NAV), []);

    // iter100 — poll the presence roster every 30s so the "Who's online"
    // nav item can show a live count + pulsing dot without opening the page.
    // Silent failures; poll only runs while the admin is authenticated.
    const [onlineCount, setOnlineCount] = useState(null);
    useEffect(() => {
        // iter100.314 — presence polling gate: mirror the admin-tier
        // check below so super_admins get the online-count widget too.
        if (!user || (user.role !== "admin" && user.role !== "super_admin" && !user.is_super_admin)) return;
        let cancelled = false;
        const fetchCount = async () => {
            try {
                const { data } = await api.get("/admin/presence?status=online");
                if (!cancelled) setOnlineCount(data?.counts?.online ?? 0);
            } catch {
                /* silent */
            }
        };
        fetchCount();
        const t = setInterval(fetchCount, 30_000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user]);

    if (loading) return <div className="p-12 text-center text-muted-foreground">Loading...</div>;
    if (!user) {
        // 2026-02-28 — logout-flow uniform "→ /". The shells' navigate("/")
        // can lose the race against this guard's <Navigate>; the session
        // flag set by AuthContext.logout() lets us cooperate: redirect to
        // "/" on logout, but keep the helpful "/admin-login" redirect for
        // direct-URL visits by anonymous users.
        const isLoggingOut = sessionStorage.getItem("isLoggingOut") === "1";
        if (isLoggingOut) sessionStorage.removeItem("isLoggingOut");
        // iter98.99dh — Always land on the portal's own login screen.
        return <Navigate to="/admin-login" replace />;
    }
    // iter100.314 — Accept BOTH admin and super_admin roles. Mirrors the
    // backend `require_admin` decorator relaxation from iter100.144fx.
    // Without this, super-admins (e.g. Uday Ratthod, role='super_admin')
    // get silently redirected to '/' when hitting any /admin/* URL even
    // though the backend accepts their token on every /api/admin/* call.
    const isAdminTier = user.role === "admin"
        || user.role === "super_admin"
        || user.is_super_admin === true;
    if (!isAdminTier) return <Navigate to="/" replace />;

    return (
        <div className="min-h-screen bg-secondary/30" data-testid="admin-layout">
            <header className="bg-card border-b border-border sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16">
                    <div className="flex items-center gap-4">
                        <InvisiqueLogo size="sm" />
                        <div className="hidden sm:flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] tracking-[0.2em] uppercase font-semibold">
                            <Crown size={10} weight="fill" /> Admin
                        </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <ReviewQueueBell />
                        <EscalationsBell />
                        <Button
                            size="sm"
                            className="rounded-full bg-primary hover:bg-primary/90"
                            onClick={() => setInviteOpen(true)}
                            data-testid="admin-invite-user-btn"
                        >
                            <UserPlus size={14} weight="fill" className="mr-1.5" /> Invite user
                        </Button>
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-medium">{user.name}</div>
                            <div className="text-xs text-muted-foreground">{user.phone}</div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/admin-login"); }} data-testid="admin-logout-btn">
                            <SignOut size={14} className="mr-1" /> Logout
                        </Button>
                    </div>
                </div>
            </header>

            <InviteUserDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />

            <div className={`mx-auto px-5 sm:px-8 py-8 flex ${collapsed ? "gap-3 lg:gap-4" : "gap-6 lg:gap-8"} ${collapsed ? "max-w-[1600px]" : "max-w-7xl"}`}>
                <aside className={`shrink-0 transition-[width] duration-200 ${collapsed ? "w-16" : "w-64"}`} data-testid="admin-sidebar" data-collapsed={collapsed ? "true" : "false"}>
                    {/* iter100.77 — Sidebar becomes a fixed-height sticky rail
                        with an internal scroll region. Collapse toggle stays
                        pinned at top; only the nav list scrolls (thin
                        idle scrollbar). */}
                    <div className={`bg-card border border-border rounded-2xl sticky top-6 lg:top-20 flex flex-col ${collapsed ? "p-1.5" : "p-3"}`} style={{ maxHeight: "calc(100vh - 3rem)" }}>
                        {/* iter100.267 — Old "◄ COLLAPSE" text button
                            above the Overview nav item was removed;
                            now lives at the footer of the sidebar as a
                            compact teal icon toggle (universal design
                            shared with PortalLayout + CreatorShell). */}
                        <nav className="space-y-1 flex-1 min-h-0 overflow-y-auto sidebar-scroll pr-1 -mr-1" data-testid="admin-sidebar-nav-scroll">
                            {grouped.map((group) => {
                                const isFolded = !collapsed && group.label && groupsCollapsed.has(group.label);
                                const groupSlug = (group.label || "ungrouped").toLowerCase().replace(/\s/g, "-");
                                const isDangerGroup = group.items.some((it) => it.danger);
                                return (
                                    <React.Fragment key={group.label || "ungrouped"}>
                                        {/* Collapsed icon-rail: show only a divider between groups. */}
                                        {collapsed && group.items[0]?.groupStart && (
                                            <div className="my-1.5 h-px bg-border/60" aria-hidden />
                                        )}
                                        {/* Expanded: clickable group header with chevron. */}
                                        {!collapsed && group.label && (
                                            <button
                                                type="button"
                                                onClick={() => toggleGroup(group.label)}
                                                aria-expanded={!isFolded}
                                                aria-controls={`admin-nav-group-body-${groupSlug}`}
                                                className={`w-full pt-3 pb-1.5 px-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.14em] transition-colors group ${
                                                    isDangerGroup
                                                        ? "text-rose-500/80 hover:text-rose-600"
                                                        : "text-muted-foreground/70 hover:text-foreground"
                                                }`}
                                                data-testid={`admin-nav-group-label-${groupSlug}`}
                                                title={isFolded ? `Expand ${group.label}` : `Collapse ${group.label}`}
                                            >
                                                <span>{group.label}</span>
                                                <CaretDown
                                                    size={10}
                                                    weight="bold"
                                                    className={`transition-transform ${isFolded ? "-rotate-90" : "rotate-0"} opacity-60 group-hover:opacity-100`}
                                                />
                                            </button>
                                        )}
                                        {/* Group body — items. Hidden entirely when the group is folded (except when it's the active group). */}
                                        {(!isFolded || collapsed) && (
                                            <div id={`admin-nav-group-body-${groupSlug}`} className="space-y-1">
                                                {group.items.map((n) => {
                                                    if (n.children) {
                                                        return <SubmenuItem key={n.label} item={n} collapsed={collapsed} />;
                                                    }
                                                    const dangerActive = "bg-rose-600 text-white hover:bg-rose-700";
                                                    const dangerIdle = "text-rose-700 hover:bg-rose-50";
                                                    const normalActive = "bg-primary text-white";
                                                    const normalIdle = "text-foreground hover:bg-secondary";
                                                    return (
                                                        <NavLink
                                                            key={n.to}
                                                            to={n.to}
                                                            end={n.end}
                                                            title={collapsed ? n.label : undefined}
                                                            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                                            className={({ isActive }) =>
                                                                `flex items-center transition-colors ${
                                                                    collapsed
                                                                        ? "relative justify-center h-10 rounded-xl"
                                                                        : "gap-3 px-3 py-2.5 rounded-xl text-sm"
                                                                } ${
                                                                    n.danger
                                                                        ? (isActive ? dangerActive : dangerIdle)
                                                                        : (isActive ? normalActive : normalIdle)
                                                                }`
                                                            }
                                                        >
                                                            <n.icon size={18} weight="duotone" />
                                                            {!collapsed && <span className="flex-1">{n.label}</span>}
                                                            {!collapsed && n.to === "/admin/presence" && onlineCount !== null && onlineCount > 0 && (
                                                                <span
                                                                    className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold border border-emerald-200"
                                                                    data-testid="admin-nav-online-badge"
                                                                    title={`${onlineCount} user${onlineCount === 1 ? "" : "s"} online right now`}
                                                                >
                                                                    <span className="relative inline-flex h-1.5 w-1.5">
                                                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                                                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                                                    </span>
                                                                    {onlineCount}
                                                                </span>
                                                            )}
                                                            {collapsed && n.to === "/admin/presence" && onlineCount !== null && onlineCount > 0 && (
                                                                <span
                                                                    className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-card"
                                                                    title={`${onlineCount} online`}
                                                                />
                                                            )}
                                                        </NavLink>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </nav>

                        {/* iter100.267 — Universal footer collapse toggle
                            (matches PortalLayout + CreatorShell). Teal
                            circular icon button; glyph rotates 180° when
                            collapsed. */}
                        <button
                            type="button"
                            onClick={() => setCollapsed((v) => !v)}
                            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                            aria-pressed={collapsed}
                            className={`mt-3 self-center flex items-center justify-center w-8 h-8 rounded-full border border-primary/25 text-primary bg-primary/5 hover:bg-primary/15 hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors ${collapsed ? "rotate-180" : ""}`}
                            data-testid="admin-sidebar-collapse-toggle"
                        >
                            <SidebarSimple size={16} weight="duotone" />
                        </button>
                    </div>
                </aside>
                <main className="flex-1 min-w-0">
                    <ErrorBoundary routeLabel="this admin page">
                        {/* iter101.87 — Expose sidebar collapsed state
                            to nested admin pages so any page that
                            wants to widen its content on collapse can
                            do so via `useOutletContext`. */}
                        <Outlet context={{ sidebarCollapsed: collapsed }} />
                    </ErrorBoundary>
                </main>
            </div>
        </div>
    );
}
